<template>
  <div :class="$style.projectSelect" @click.stop>
    <ComboboxRoot
      v-model="selectedProjectId"
      v-model:open="isOpen"
      :filter-function="filterProjects"
      :open-on-focus="true"
      :ignore-filter="true"
      @update:model-value="handleProjectChange"
    >
      <ComboboxAnchor :class="$style.comboboxAnchor">
        <ComboboxInput
          :class="$style.comboboxInput"
          :value="inputValue"
          @input="handleInput"
          @keydown.enter="handleEnterKey"
          @blur="handleBlur"
          placeholder="Type to search projects..."
        />
      </ComboboxAnchor>
      <ComboboxContent
        :class="$style.comboboxContent"
        position="popper"
        @pointer-down-outside="handleClickOutside"
      >
        <ComboboxViewport :class="$style.comboboxViewport">
          <ComboboxItem
            v-for="project in filteredProjects"
            :key="project.id"
            :value="project.id"
            :class="$style.comboboxItem"
          >
            {{ project.name }}
          </ComboboxItem>
          <div
            v-if="inputValue && !exactMatch && inputValue.trim()"
            :class="$style.createNewItem"
            @click="createNewProject"
          >
            + Create "{{ inputValue }}"
          </div>
          <div
            v-if="selectedProjectId && !inputValue"
            :class="$style.clearItem"
            @click="clearProject"
          >
            Clear selection
          </div>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxRoot>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import {
  ComboboxRoot,
  ComboboxAnchor,
  ComboboxInput,
  ComboboxContent,
  ComboboxViewport,
  ComboboxItem,
} from "reka-ui";
import { projectService } from "../services/projectService";

const props = defineProps<{
  modelValue?: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", projectId?: string): void;
}>();

const selectedProjectId = ref<string | undefined>(props.modelValue);
const inputValue = ref("");
const isOpen = ref(false);

const availableProjects = computed(() => projectService.getProjects());

const selectedProject = computed(() =>
  selectedProjectId.value
    ? availableProjects.value.find((p) => p.id === selectedProjectId.value)
    : undefined
);

// Update input value when selected project changes externally
watch(
  () => props.modelValue,
  (newValue) => {
    selectedProjectId.value = newValue;
    if (newValue) {
      const project = availableProjects.value.find((p) => p.id === newValue);
      inputValue.value = project?.name || "";
    } else {
      inputValue.value = "";
    }
  },
  { immediate: true }
);

// Update input value when selected project changes internally
watch(selectedProject, (project) => {
  if (project) {
    inputValue.value = project.name;
  } else {
    inputValue.value = "";
  }
});

const exactMatch = computed(() => {
  if (!inputValue.value.trim()) return false;
  return availableProjects.value.some(
    (p) => p.name.toLowerCase() === inputValue.value.toLowerCase().trim()
  );
});

const filteredProjects = computed(() => {
  if (!inputValue.value.trim()) {
    return availableProjects.value;
  }
  const searchTerm = inputValue.value.toLowerCase().trim();
  return availableProjects.value.filter((project) =>
    project.name.toLowerCase().includes(searchTerm)
  );
});

function filterProjects(items: any[]) {
  // Custom filter function - we handle filtering in computed property
  return items;
}

function handleClickOutside() {
  // Close the dropdown when clicking outside
  // The @pointer-down-outside event only fires when Reka UI detects a click outside
  // Since we're controlling the open state with v-model:open, we need to close manually
  isOpen.value = false;
}

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  inputValue.value = target.value;
  // Clear selection if user is typing something different
  if (selectedProjectId.value) {
    const currentProject = availableProjects.value.find(
      (p) => p.id === selectedProjectId.value
    );
    if (currentProject?.name !== inputValue.value) {
      selectedProjectId.value = undefined;
      emit("update:modelValue", undefined);
    }
  }
}

function handleProjectChange(value: string | undefined) {
  selectedProjectId.value = value;
  if (value) {
    const project = availableProjects.value.find((p) => p.id === value);
    if (project) {
      inputValue.value = project.name;
    }
  }
  emit("update:modelValue", value);
}

function createNewProject() {
  if (!inputValue.value.trim()) return;
  try {
    const newProject = projectService.createProject(inputValue.value.trim());
    selectedProjectId.value = newProject.id;
    inputValue.value = newProject.name;
    emit("update:modelValue", newProject.id);
  } catch (error) {
    console.error("Error creating project:", error);
  }
}

function clearProject() {
  selectedProjectId.value = undefined;
  inputValue.value = "";
  emit("update:modelValue", undefined);
}

function handleEnterKey(event: KeyboardEvent) {
  // If there's input value and no exact match, create the project
  if (inputValue.value.trim() && !exactMatch.value) {
    event.preventDefault();
    createNewProject();
  }
}

function handleBlur() {
  // When input loses focus, ensure project is created if user typed something
  const ensuredProjectId = ensureProjectCreated();
  // Emit the ensured projectId (might be different from selectedProjectId if project was just created)
  if (ensuredProjectId !== selectedProjectId.value) {
    emit("update:modelValue", ensuredProjectId);
  }
}

// Expose method to ensure project is created/selected before form submission
function ensureProjectCreated(): string | undefined {
  const trimmedInput = inputValue.value.trim();
  if (!trimmedInput) {
    // No input, clear selection
    if (selectedProjectId.value) {
      clearProject();
    }
    return undefined;
  }

  // If there's an exact match, select it (even if something else is currently selected)
  const exactMatchProject = availableProjects.value.find(
    (p) => p.name.toLowerCase() === trimmedInput.toLowerCase()
  );
  if (exactMatchProject) {
    if (selectedProjectId.value !== exactMatchProject.id) {
      selectedProjectId.value = exactMatchProject.id;
      inputValue.value = exactMatchProject.name;
      emit("update:modelValue", exactMatchProject.id);
    }
    return exactMatchProject.id;
  }

  // If no exact match, create new project (even if something else is currently selected)
  // This handles the case where user typed a new project name
  try {
    const newProject = projectService.createProject(trimmedInput);
    selectedProjectId.value = newProject.id;
    inputValue.value = newProject.name;
    emit("update:modelValue", newProject.id);
    return newProject.id;
  } catch (error) {
    console.error("Error creating project:", error);
    // If creation fails (e.g., duplicate), try to find existing project
    const existingProject = projectService.getProjectByName(trimmedInput);
    if (existingProject) {
      selectedProjectId.value = existingProject.id;
      inputValue.value = existingProject.name;
      emit("update:modelValue", existingProject.id);
      return existingProject.id;
    }
    return selectedProjectId.value;
  }
}

// Expose the method for parent components
defineExpose({
  ensureProjectCreated,
});
</script>

<style module>
.projectSelect {
  position: relative;
  min-width: 200px;
}

.comboboxAnchor {
  display: inline-flex;
  align-items: center;
  min-width: 200px;
  border-radius: 4px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  transition: all 0.2s ease;
}

.comboboxAnchor:focus-within {
  background: rgba(255, 255, 255, 0.3);
  outline: 1px solid var(--primary-color);
}

.comboboxInput {
  flex: 1;
  width: 100%;
  background: transparent;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  color: white;
  font-size: 16px;
  font-family: inherit;
  outline: none;
}

.comboboxInput::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.comboboxContent {
  background: #2c3e50;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  z-index: 10001 !important;
  min-width: 200px;
  overflow: hidden;
}

.comboboxViewport {
  padding: 4px;
  max-height: 200px;
  overflow-y: auto;
  list-style: none;
  margin: 0;
}

.comboboxItem {
  padding: 8px 12px;
  color: white;
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.9em;
}

.comboboxItem:hover,
.comboboxItem[data-highlighted] {
  background: rgba(255, 255, 255, 0.2);
}

.comboboxItem[data-selected] {
  background: var(--primary-color);
  color: white;
}

.createNewItem {
  padding: 8px 12px;
  color: var(--primary-color);
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.9em;
  font-weight: 500;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 4px;
}

.createNewItem:hover {
  background: rgba(255, 255, 255, 0.1);
}

.clearItem {
  padding: 8px 12px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.9em;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 4px;
}

.clearItem:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

/* Global styles for portaled combobox content to ensure it appears above dialogs */
:global([data-reka-combobox-content]),
:global([data-radix-popper-content-wrapper]) {
  z-index: 10001 !important;
}
</style>
